package com.karyadina.ui.theme

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.AnimationVector1D
import androidx.compose.animation.core.tween
import androidx.compose.runtime.*
import kotlin.math.sin
import kotlin.math.PI
import com.karyadina.util.currentTimeMillis

/**
 * Centralized animation utilities for flashcard UI components
 * Provides state management, rotation/opacity calculations, and reusable animation helpers
 */
object FlashcardAnimations {
    // Animation timing constants
    object Timings {
        const val CARD_FLIP_DURATION = 400  // ms
        const val STAGGER_DELAY = 50  // ms per item
        const val PULSE_DURATION = 1500  // ms
        const val SUCCESS_DURATION = 600  // ms
    }

    /**
     * Card flip state tracker for 3D rotation animations
     */
    class CardFlipState(initialShowAnswer: Boolean = false) {
        private val _rotationProgress = Animatable(if (initialShowAnswer) 1f else 0f)
        val rotationProgress: Float get() = _rotationProgress.value

        suspend fun toggleFlip() {
            val targetValue = if (rotationProgress < 0.5f) 1f else 0f
            _rotationProgress.animateTo(
                targetValue = targetValue,
                animationSpec = tween(durationMillis = Timings.CARD_FLIP_DURATION)
            )
        }
    }

    /**
     * Success feedback state for celebration animation
     */
    class SuccessFeedbackState {
        private val _scale = Animatable(0f)
        private val _rotation = Animatable(0f)
        private val _alpha = Animatable(1f)

        // Expose as composable derived states for proper recomposition
        @Composable
        fun getScale(): Float = _scale.value

        @Composable
        fun getRotation(): Float = _rotation.value

        @Composable
        fun getAlpha(): Float = _alpha.value

        suspend fun trigger() {
            // Scale up
            _scale.animateTo(1.2f, animationSpec = tween(durationMillis = 300))
            _scale.animateTo(1f, animationSpec = tween(durationMillis = 100))
            
            // Rotate a full spin
            _rotation.animateTo(360f, animationSpec = tween(durationMillis = Timings.SUCCESS_DURATION))
            
            // Fade out and reset
            _alpha.animateTo(0f, animationSpec = tween(durationMillis = 200))
        }

        suspend fun reset() {
            _scale.snapTo(0f)
            _rotation.snapTo(0f)
            _alpha.snapTo(1f)
        }
    }

    /**
     * Get the 3D Y-axis rotation angle for card flip effect
     * @param progress Float from 0 to 1 representing animation progress
     * @return Rotation angle in degrees (0 to 180)
     */
    fun getFlipRotation(progress: Float): Float {
        return progress * 180f
    }

    /**
     * Get opacity value for cross-fade effect during card flip
     * Question fades out as progress goes from 0 to 0.5
     * Answer fades in as progress goes from 0.5 to 1
     * @param progress Float from 0 to 1
     * @param showingAnswer Boolean indicating which side this opacity is for
     * @return Alpha value from 0 to 1
     */
    fun getFlipOpacity(progress: Float, showingAnswer: Boolean): Float {
        return if (showingAnswer) {
            // Answer side: 0 at start, 1 after halfway through flip
            (progress - 0.5f) * 2f.coerceIn(0f, 1f)
        } else {
            // Question side: 1 at start, 0 after halfway through flip
            (1f - progress) * 2f.coerceIn(0f, 1f)
        }
    }

    /**
     * Get pulse animation alpha for due cards
     * Creates a gentle pulsing effect with ~1.5s cycle
     * @return Alpha value oscillating between 0.6 and 1.0
     */
    fun getPulseAlpha(): Float {
        val currentTime = currentTimeMillis() % Timings.PULSE_DURATION
        val normalizedTime = (currentTime.toFloat() / Timings.PULSE_DURATION.toFloat()) * (2 * PI.toFloat())
        // Sine wave: oscillates between -1 and 1, then map to 0.6-1.0
        return 0.8f + (0.2f * sin(normalizedTime))
    }

    /**
     * Get staggered list animation with delay based on index
     * Each item animates in sequence with 50ms spacing
     * @param itemIndex Index of the item in the list
     * @return Delay in milliseconds
     */
    fun getStaggeredDelay(itemIndex: Int): Long {
        return (itemIndex * Timings.STAGGER_DELAY).toLong()
    }

    /**
     * Calculate shake offset for error feedback
     * Rapid horizontal oscillation for 400ms
     * @param progress Float from 0 to 1
     * @return Horizontal offset in DP units
     */
    fun getShakeOffset(progress: Float): Float {
        val shakes = 8 // Number of oscillations
        val oscillation = sin(progress * shakes * PI.toFloat())
        return oscillation * 8f // Max 8dp movement
    }

    /**
     * Success scale animation curve
     * Bouncy scale-up effect: 0 -> 1.2 -> 1.0
     * @param progress Float from 0 to 1
     * @return Scale multiplier
     */
    fun getSuccessScale(progress: Float): Float {
        return if (progress < 0.75f) {
            // Scale up to 1.2
            1f + (0.2f * (progress / 0.75f))
        } else {
            // Scale back to 1.0
            1.2f - (0.2f * ((progress - 0.75f) / 0.25f))
        }
    }

    /**
     * Success rotation animation
     * Full 360 degree spin
     * @param progress Float from 0 to 1
     * @return Rotation in degrees
     */
    fun getSuccessRotation(progress: Float): Float {
        return progress * 360f
    }

    /**
     * Compose function to create a reusable staggered enter transition
     * Returns a combined animation of slide + fade with proper timing
     * This is designed to be used with AnimatedVisibility
     */
    @Composable
    fun rememberStaggeredEnterTransition(): Long {
        return 0L  // Will be applied per-item with calculated delay
    }
}
